import { Link } from "react-router-dom";
import { Pause } from "lucide-react";
import type { GroomingQueuePauseReason, GroomingQueueState } from "@ttc-spectree/toolbox-contract";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import { GROOMING_QUEUE_PATH } from "@/lib/grooming/paths";
import { cn } from "@/lib/utils";

/**
 * The title bar's run-queue chip (epic PI-SPEC-SW-E284, feature
 * PI-SPEC-SW-1440), beside the Claude chip: "Running · 2 queued" while the
 * queue works, "Paused · usage limit" (amber for setup problems and the
 * usage limit), "3 waiting · Resume" after a relaunch, and nothing when the
 * queue is empty, in a browser or with a contract-2 app. Clicking it opens
 * the Queue screen.
 *
 * A setup pause (epic PI-SPEC-SW-E290, feature PI-SPEC-SW-1479, frame B2)
 * reads "Paused · 5 waiting" in amber with a pause icon, so the person sees
 * the stop from any page; the Queue page says why and offers Fix it.
 */

/** A few words for each pause reason the chip names, for the chip. */
export const PAUSE_SHORT: Readonly<Record<Exclude<GroomingQueuePauseReason, "launch" | "setup">, string>> = {
  sleep: "computer slept",
  "usage-limit": "usage limit",
  user: "stopped by you",
};

/** True while the queue waits for a setup fix: the chip and the rail's Queue badge turn amber. Pure; exported for tests. */
export function isSetupPaused(state: GroomingQueueState | null): boolean {
  return state?.status === "paused" && state.pause?.reason === "setup";
}

/** "Paused · 5 waiting": what a setup pause holds up (running and waiting). */
export function pausedWaitingText(waiting: number): string {
  return `Paused · ${String(waiting)} waiting`;
}

export interface QueueChipView {
  text: string;
  /** Amber when the queue cannot go on without the person (setup) or is waiting for the usage window. */
  tone: "normal" | "amber";
  /** Running now: the dot pulses. */
  live: boolean;
  /** A setup pause shows a pause icon instead of the dot. */
  icon?: "pause";
}

/** What the chip shows for a queue state, or null to hide it. Pure; exported for tests. */
export function queueChipView(state: GroomingQueueState | null): QueueChipView | null {
  if (!state) return null;
  const running = state.running.length;
  const waiting = state.upNext.length;
  if (running === 0 && waiting === 0) return null;
  if (state.status === "paused" && state.pause) {
    if (state.pause.reason === "launch") {
      return { text: `${String(waiting)} waiting · Resume`, tone: "normal", live: false };
    }
    if (state.pause.reason === "setup") {
      return { text: pausedWaitingText(running + waiting), tone: "amber", live: false, icon: "pause" };
    }
    const amber = state.pause.reason === "usage-limit";
    return { text: `Paused · ${PAUSE_SHORT[state.pause.reason]}`, tone: amber ? "amber" : "normal", live: false };
  }
  if (running > 0) {
    return { text: waiting > 0 ? `Running · ${String(waiting)} queued` : "Running", tone: "normal", live: true };
  }
  return { text: `${String(waiting)} queued`, tone: "normal", live: false };
}

/** The chip, or nothing. */
export function QueueChip() {
  const { state } = useGroomingQueue();
  const view = queueChipView(state);
  if (!view) return null;
  return (
    <Link
      to={GROOMING_QUEUE_PATH}
      className={cn(
        "flex flex-none items-center gap-[7px] whitespace-nowrap rounded-full border py-[3px] pl-2 pr-2.5 text-xs transition-colors [-webkit-app-region:no-drag]",
        view.tone === "amber"
          ? "border-[#F59E0B]/40 bg-[#F59E0B]/[0.08] text-[#FCD34D] hover:border-[#F59E0B]/60"
          : "border-[#1F1F23] text-[#A1A1AA] hover:border-[#2A2A2E] hover:text-[#FAFAFA]"
      )}
      title="Open the queue"
      aria-label={`Queue: ${view.text}. Open the queue`}
      data-testid="queue-chip"
      data-tone={view.tone}
    >
      {view.icon === "pause" ? (
        <Pause className="h-3 w-3 text-[#F59E0B]" aria-hidden="true" data-testid="queue-chip-pause" />
      ) : (
        <span
          className={cn(
            "h-[7px] w-[7px] rounded-full",
            view.tone === "amber" ? "bg-[#F59E0B]" : view.live ? "bg-sky-400 motion-safe:animate-pulse" : "bg-[#71717A]"
          )}
          aria-hidden="true"
        />
      )}
      {view.text}
    </Link>
  );
}
